import React from 'react';
import BlurSkeleton from './BlurSkeleton';
import SkeletonRegion from './SkeletonRegion';
import useStyles from './TabSkeletons.styles';

/**
 * Route-level skeletons. They are rendered as the lazy-route Suspense fallback
 * so the chunk load and the page's own loading state show the same shape, with
 * no intermediate generic placeholder.
 */

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const classes = useStyles();
  return (
    <div className="page-content">
      <SkeletonRegion className={classes.page}>{children}</SkeletonRegion>
    </div>
  );
};

const PageHeader: React.FC = () => {
  const classes = useStyles();
  return (
    <div className={classes.pageHeader}>
      <BlurSkeleton height={30} width={280} />
      <BlurSkeleton height={13} width={380} delay={70} />
    </div>
  );
};

/** Current reservation card, upcoming list and the history table. */
export const MyReservationsPageSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <PageShell>
      <PageHeader />
      <div className={classes.card}>
        <BlurSkeleton height={16} width="35%" />
        <BlurSkeleton variant="rect" height={120} radius={12} delay={80} />
      </div>
      <div className={classes.card}>
        <BlurSkeleton height={16} width="30%" />
        {Array.from({ length: 3 }, (_, index) => (
          <div className={classes.row} key={index}>
            <BlurSkeleton variant="rect" height={48} radius={10} className={classes.grow} delay={index * 100} />
            <BlurSkeleton variant="rect" height={34} width={110} radius={999} delay={index * 100 + 60} />
          </div>
        ))}
      </div>
      <div className={classes.card}>
        <div className={classes.row}>
          <BlurSkeleton height={16} width={200} />
          <BlurSkeleton variant="rect" height={36} width={150} radius={10} delay={80} />
        </div>
        {Array.from({ length: 5 }, (_, index) => (
          <BlurSkeleton key={index} variant="rect" height={44} radius={10} delay={index * 90} />
        ))}
      </div>
    </PageShell>
  );
};

/** Profile hero, tab bar and the activity panels underneath. */
export const ProfilePageSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <PageShell>
      <div className={classes.card}>
        <div className={classes.row}>
          <BlurSkeleton variant="circle" height={76} />
          <div className={classes.grow}>
            <BlurSkeleton height={24} width="38%" delay={60} />
            <BlurSkeleton height={12} width="26%" delay={110} />
          </div>
        </div>
        <div className={classes.chipRow}>
          <BlurSkeleton variant="rect" height={28} width={140} radius={999} delay={160} />
          <BlurSkeleton variant="rect" height={28} width={120} radius={999} delay={210} />
        </div>
      </div>
      <div className={classes.row}>
        <BlurSkeleton variant="rect" height={34} width={120} radius={999} />
        <BlurSkeleton variant="rect" height={34} width={120} radius={999} delay={70} />
      </div>
      <div className={classes.cardGrid}>
        {[0, 1, 2].map((index) => (
          <div className={classes.card} key={index}>
            <BlurSkeleton height={11} width="55%" delay={index * 110} />
            <BlurSkeleton height={30} width="40%" delay={index * 110 + 60} />
          </div>
        ))}
      </div>
      <div className={classes.card}>
        <BlurSkeleton height={16} width="30%" />
        {Array.from({ length: 4 }, (_, index) => (
          <BlurSkeleton key={index} variant="rect" height={44} radius={10} delay={index * 90} />
        ))}
      </div>
    </PageShell>
  );
};

/** Asset control subnav, filter row and the asset table. */
export const AssetControlPageSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <PageShell>
      <div className={classes.row}>
        <BlurSkeleton variant="rect" height={38} width={150} radius={999} />
        <BlurSkeleton variant="rect" height={38} width={130} radius={999} delay={70} />
        <BlurSkeleton variant="rect" height={38} width={140} radius={999} delay={140} />
      </div>
      <BlurSkeleton variant="rect" height={46} radius={10} delay={180} />
      <div className={classes.card}>
        <div className={classes.row}>
          <BlurSkeleton variant="rect" height={40} width={180} radius={10} />
          <BlurSkeleton variant="rect" height={40} width={180} radius={10} delay={80} />
          <BlurSkeleton variant="rect" height={40} width={160} radius={10} delay={160} />
        </div>
        {Array.from({ length: 7 }, (_, index) => (
          <BlurSkeleton key={index} variant="rect" height={40} radius={8} delay={index * 80} />
        ))}
      </div>
    </PageShell>
  );
};

/** Meeting rooms: date picker over the room cards. */
export const MeetingRoomsPageSkeleton: React.FC = () => {
  const classes = useStyles();
  return (
    <PageShell>
      <PageHeader />
      <div className={classes.row}>
        <BlurSkeleton variant="rect" height={46} width={220} radius={10} />
        <BlurSkeleton variant="rect" height={46} width={180} radius={10} delay={80} />
      </div>
      <div className={classes.peopleGrid}>
        {Array.from({ length: 6 }, (_, index) => (
          <div className={classes.card} key={index}>
            <BlurSkeleton variant="rect" height={110} radius={12} delay={index * 90} />
            <BlurSkeleton height={14} width="60%" delay={index * 90 + 50} />
            <BlurSkeleton height={11} width="40%" delay={index * 90 + 100} />
          </div>
        ))}
      </div>
    </PageShell>
  );
};

/** Admin dashboard header and tab bar, shown before the active tab resolves. */
export const AdminShellSkeleton: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const classes = useStyles();
  return (
    <PageShell>
      <PageHeader />
      <div className={classes.row}>
        {[0, 1, 2, 3, 4].map((index) => (
          <BlurSkeleton
            key={index}
            variant="rect"
            height={38}
            width={120 + ((index * 27) % 50)}
            radius={999}
            delay={index * 60}
          />
        ))}
      </div>
      {children}
    </PageShell>
  );
};

/** Reports header and tab bar, shown before the active report resolves. */
export const ReportsShellSkeleton: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const classes = useStyles();
  return (
    <PageShell>
      <PageHeader />
      <div className={classes.row}>
        {[0, 1, 2].map((index) => (
          <BlurSkeleton
            key={index}
            variant="rect"
            height={36}
            width={130}
            radius={999}
            delay={index * 70}
          />
        ))}
      </div>
      {children}
    </PageShell>
  );
};

/** Talent header and tab bar, shown before the active tab resolves. */
export const TalentShellSkeleton: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const classes = useStyles();
  return (
    <PageShell>
      <div className={classes.row}>
        {[0, 1, 2, 3, 4].map((index) => (
          <BlurSkeleton
            key={index}
            variant="rect"
            height={36}
            width={110 + ((index * 31) % 60)}
            radius={999}
            delay={index * 60}
          />
        ))}
      </div>
      {children}
    </PageShell>
  );
};
